import React, { useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Button } from 'transfix-ui/components/Button'
import { Spinner } from 'transfix-ui/components/Spinner'
import { ITabsProps, Tabs } from 'transfix-ui/components/Tabs'
import { Box, Typography, createStyles, makeStyles } from 'transfix-ui/core'
import { brandColors, shadows, theme } from 'transfix-ui/theme'
import {
  IInboundConnectionConfig,
  IOutboundConnectionConfig,
  IPartnerApplicationTypes,
} from 'types/graphqlTypes'
import { StringParam } from 'use-query-params'
import { useQueryParams } from 'utils/hooks'
import { useEdiInboundConnectionConfigsQuery } from '../ConnectionConfigs/InboundConnectionConfig/graphql/EdiInboundConnectionConfigs'
import { useEdiOutboundConnectionConfigsQuery } from '../ConnectionConfigs/OutboundConnectionConfig/graphql/EdiOutboundConnectionConfigs'
import { EDI_TOOLS_ROUTES } from '../LandingPage'
import { InboundIntegrationDetails } from './Inbound/InboundIntegrationDetails'
import { OutboundIntegrationDetails } from './Outbound/OutboundIntegrationDetails'
import { IntegrationOverview } from './Overview/IntegrationOverview'
import { ShipperEdiSettingsTab } from './Settings/ShipperEdiSettingsTab'
import {
  INBOUND_TAB,
  INTEGRATION_OVERVIEW_TAB,
  OUTBOUND_TAB,
  SETTINGS_TAB,
  SHIPPER_CONFIG_TABS,
} from './constants'

const useShipperConfigDetailsStyles = makeStyles(() =>
  createStyles({
    card: {
      padding: theme.spacing(1),
      marginBottom: theme.spacing(2),
      boxShadow: shadows[3],
    },
    titleText: {
      display: 'flex',
      flex: theme.spacing(1),
      alignItems: 'center',
    },
    secondary: {
      color: brandColors.coolGray5,
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    paginationContainer: {
      textAlign: 'center',
      marginTop: theme.spacing(8),
    },
    tabs: {
      '& > div[role=tabpanel]': {
        position: 'relative',
      },
      '& > .MuiTabs-root': {
        paddingRight: '30%',
      },
    },
  })
)

export const ShipperConfigDetails = () => {
  const { id: shipperId } = useParams<{ id: string }>()
  const { name: shipperName } = useParams<{ name: string }>()
  const history = useHistory()
  const [{ tab }, setQueryParams] = useQueryParams({
    tab: StringParam,
  })

  const activeTabParam = tab || INTEGRATION_OVERVIEW_TAB
  const [activeTab, setActiveTab] = useState<ITabsProps['activeTabId']>(activeTabParam)
  const classes = useShipperConfigDetailsStyles()

  const { data: outboundData, loading: outboundLoading } = useEdiOutboundConnectionConfigsQuery({
    variables: {
      partnerApplicationId: shipperId,
      partnerApplicationType: IPartnerApplicationTypes.ShipperApplication,
    },
    notifyOnNetworkStatusChange: true,
  })
  const { ediOutboundConnectionConfigs } = outboundData || {}
  let outboundConfigs = ediOutboundConnectionConfigs?.nodes
  outboundConfigs = outboundConfigs?.filter(() => (element: IOutboundConnectionConfig) => {
    // remove any undefined values
    return element !== undefined
  })
  const outboundConfigIds = outboundConfigs?.map(config => config?.id || '')

  const { data: inboundData, loading: inboundLoading } = useEdiInboundConnectionConfigsQuery({
    variables: {
      partnerApplicationId: shipperId,
      partnerApplicationType: IPartnerApplicationTypes.ShipperApplication,
    },
    notifyOnNetworkStatusChange: true,
  })

  const { ediInboundConnectionConfigs } = inboundData || {}
  let inboundConfigs = ediInboundConnectionConfigs?.nodes
  inboundConfigs = inboundConfigs?.filter(() => (element: IInboundConnectionConfig) => {
    // remove any undefined values
    return element !== undefined
  })
  const inboundConfigIds = inboundConfigs?.map(config => config?.id || '')

  const handleSetCurrentTab = (newTab: string) => {
    setQueryParams({
      tab: newTab,
    })
    setActiveTab(newTab)
  }

  const shipperContactsClickHandler = () => {
    history.push(
      `${EDI_TOOLS_ROUTES.SHIPPER_CONFIG}/${shipperId}${EDI_TOOLS_ROUTES.SHIPPER_CONTACTS}`
    )
  }

  const mapperFilesClickHandler = () => {
    history.push(`${EDI_TOOLS_ROUTES.MAPPER_FILES}?shipperId=${shipperId}`)
  }

  return (
    <div>
      <Box mb={3} data-test='SidePanel'>
        <Box display='flex' justifyContent='space-between' alignItems='center' pl={2} pr={2}>
          <Typography variant='h2' className={classes.titleText}>
            <span className='mr3' data-test='inbound-connection-config-identifier-header'>
              {shipperName}
            </span>
          </Typography>
          <Box display='flex' justifyContent='space-between' gap={2}>
            <Button color='secondary' onClick={shipperContactsClickHandler}>
              Shipper Contacts
            </Button>
            <Button color='secondary' onClick={mapperFilesClickHandler}>
              EDI Files
            </Button>
          </Box>
        </Box>
        {(inboundLoading || outboundLoading) && <Spinner />}
        <Tabs
          key={JSON.stringify(SHIPPER_CONFIG_TABS)}
          tabList={SHIPPER_CONFIG_TABS}
          activeTabId={activeTab}
          onChange={handleSetCurrentTab}
          variant='scrollable'
          scrollButtons='auto'
          indicatorColor='primary'
          className={classes.tabs}
          textColor='primary'>
          <>
            {activeTab === INTEGRATION_OVERVIEW_TAB && (
              <IntegrationOverview
                outboundConfigIds={outboundConfigIds}
                inboundConfigIds={inboundConfigIds}
              />
            )}
            {activeTab === INBOUND_TAB && (
              <InboundIntegrationDetails inboundConfigIds={inboundConfigIds} />
            )}
            {activeTab === OUTBOUND_TAB && (
              <OutboundIntegrationDetails outboundConfigIds={outboundConfigIds} />
            )}
            {activeTab === SETTINGS_TAB && (
              <ShipperEdiSettingsTab shipperApplicationId={shipperId} />
            )}
          </>
        </Tabs>
      </Box>
    </div>
  )
}
